import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { omitBy, isUndefined, trim, isArray } from "lodash-es";
import { defer, forkJoin, iif, Observable, of, throwError } from "rxjs";
import { map } from "rxjs/operators";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import { Customer, CustomerLight } from "../models/customer/customer";
import { CustomerCreate } from "../models/customer/customer.create";
import { CustomerDelete } from "../models/customer/customer.delete";
import { CustomerLoadTags } from "../models/customer/customer.load-tags";
import { CustomerUpdate } from "../models/customer/customer.update";
import { CustomerUpdateTags } from "../models/customer/customer.update-tags";
import { sntTranslate } from "src/app/support/translate";
import { Pet, PetLight } from "src/app/backend/models/pet/pet";
import { getCustomerNominative } from "src/app/support/customer";
import { AttachmentV2 } from "src/app/backend/models/attachment/attachment";
import { stringRemoveNonAsciiChar } from "src/app/support/string";
import { patientExtend } from "src/app/backend/api/api-extend/patient.extend";
@Injectable({ providedIn: "root" })
export class CustomerBackend {
  readonly #http = inject(HttpClient);

  loadWithoutPatients(id: string): Observable<Customer> {
    return this.#http.get<Customer>(
      `${environment.snoots.api.directory}/customers/${id}`
    );
  }

  loadLightWithoutPatients(id: string): Observable<CustomerLight> {
    return this.#http.get<Customer>(
      `${environment.snoots.api.directory}/customers/${id}`,
      {
        params: {
          light: "true"
        }
      }
    );
  }

  load(id: string | undefined): Observable<Customer> {
    return forkJoin({
      customer: this.loadWithoutPatients(id!),
      pets: this.loadPatients(id)
    }).pipe(
      map(({ customer, pets }) => ({
        ...customer,
        pets
      }))
    );
  }

  loadLight(id: string | undefined): Observable<CustomerLight> {
    return forkJoin({
      customer: this.loadLightWithoutPatients(id!),
      pets: this.loadPatientsLight(id)
    }).pipe(
      map(({ customer, pets }) => ({
        ...customer,
        pets: <Pet[]>pets
      }))
    );
  }

  loadPatients(id: string | undefined): Observable<Pet[]> {
    return this.#http
      .get<Pet[]>(
        `${environment.snoots.api.directory}/customers/${id}/patients`
      )
      .pipe(map((patients) => patients.map(patientExtend)));
  }

  loadPatientsLight(id: string | undefined): Observable<PetLight[]> {
    return this.#http.get<Pet[]>(
      `${environment.snoots.api.directory}/customers/${id}/patients`,
      {
        params: {
          light: "true"
        }
      }
    );
  }

  loadPatientsAlive(id: string): Observable<Pet[]> {
    return this.#http
      .get<Pet[]>(
        `${environment.snoots.api.directory}/customers/${id}/patients`,
        {
          params: {
            deceased: false
          }
        }
      )
      .pipe(map((patients) => patients.map(patientExtend)));
  }

  loadPatientsAliveLight(id: string): Observable<PetLight[]> {
    return this.#http
      .get<PetLight[]>(
        `${environment.snoots.api.directory}/customers/${id}/patients`,
        {
          params: {
            deceased: false,
            light: "true"
          }
        }
      )
      .pipe(
        map((patients) =>
          patients.sort((left: PetLight, right: PetLight) =>
            left.name.localeCompare(right.name)
          )
        )
      );
  }

  list(
    request: Partial<PageRequest> & {
      select?: string | undefined;
    }
  ): Observable<PaginatedResult<Customer>> {
    // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
    return this.#http.get<PaginatedResult<Customer>>(
      `${environment.snoots.api.directory}/customers`,
      {
        // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues,
            select: request.select
          },
          isUndefined
        )
      }
    );
  }

  listLight(request: PageRequest): Observable<PaginatedResult<CustomerLight>> {
    // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
    return this.#http.get<PaginatedResult<Customer>>(
      `${environment.snoots.api.directory}/customers`,
      {
        // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues,
            light: true
          },
          isUndefined
        )
      }
    );
  }

  searchNotEmptyQuery(query: string): Observable<Customer[]> {
    return iif(
      () => !!query,
      defer(() => this.search(query)),
      of([])
    );
  }

  totalCount(): Observable<number> {
    return this.listLight({ page: 1, pageSize: 1 }).pipe(
      map((response) => response.totalDocs)
    );
  }

  search(name: string): Observable<Customer[]> {
    return this.#http
      .get<PaginatedResult<Customer> | Customer[]>(
        `${environment.snoots.api.directory}/customers`,
        {
          params: {
            q: name || ""
          }
        }
      )
      .pipe(
        map((result: PaginatedResult<Customer> | Customer[]) =>
          isArray(result) ? result : result.docs || []
        )
      );
  }

  searchByName(name: string): Observable<Customer[]> {
    return this.searchWithFilters({ name });
  }

  searchByPhone(phone: string, pageSize?: number): Observable<Customer[]> {
    return this.searchWithFilters({ phone, pageSize });
  }

  searchByEmail(email: string, pageSize?: number): Observable<Customer[]> {
    return this.searchWithFilters({ email, pageSize });
  }

  searchByTags(tags: string[], pageSize?: number): Observable<Customer[]> {
    return this.searchWithFilters({ tags, pageSize });
  }

  searchWithFilters(filters: {
    name?: string;
    phone?: string;
    email?: string;
    tags?: string[];
    pageSize?: number;
  }): Observable<Customer[]> {
    const name: string = trim(filters.name || "");
    const phone: string = trim(filters.phone || "");
    const email: string = trim(filters.email || "");
    const tags: string[] = filters.tags || [];

    if (!name && !phone && !email && tags.length === 0) {
      return of([]);
    }

    return this.#http
      .get<PaginatedResult<Customer> | Customer[]>(
        `${environment.snoots.api.directory}/customers`,
        {
          params: omitBy(
            {
              name: name || undefined,
              phone: phone || undefined,
              email: email || undefined,
              tags: tags.length > 0 ? tags.join(",") : undefined,
              s: filters.pageSize
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((result: PaginatedResult<Customer> | Customer[]) =>
          isArray(result) ? result : result.docs || []
        )
      );
  }

  searchLight(name: string): Observable<CustomerLight[]> {
    return this.#http
      .get<PaginatedResult<Customer> | Customer[]>(
        `${environment.snoots.api.directory}/customers`,
        {
          params: {
            q: name || "",
            light: "true"
          }
        }
      )
      .pipe(
        map((result: PaginatedResult<Customer> | Customer[]) =>
          isArray(result) ? result : result.docs || []
        )
      );
  }

  searchPaged({
    q,
    page,
    pageSize
  }: {
    q: string;
    page: number;
    pageSize: number;
  }): Observable<Customer[]> {
    return this.#http.get<Customer[]>(
      `${environment.snoots.api.directory}/customers`,
      {
        params: {
          q,
          page,
          pageSize
        }
      }
    );
  }

  searchLightPaged({
    q,
    page,
    pageSize
  }: {
    q: string;
    page: number;
    pageSize: number;
  }): Observable<CustomerLight[]> {
    return this.#http.get<CustomerLight[]>(
      `${environment.snoots.api.directory}/customers`,
      {
        params: {
          q,
          page,
          pageSize,
          light: "true"
        }
      }
    );
  }

  searchByExternalId(externalId: string): Observable<Customer[]> {
    if (!trim(externalId)) {
      return throwError("externalId undefined");
    }

    return this.#http
      .get<PaginatedResult<Customer>>(
        `${environment.snoots.api.directory}/customers`,
        {
          params: {
            externalId
          }
        }
      )
      .pipe(map((result) => result.docs));
  }

  searchBySSN(ssn: string, pageSize?: number): Observable<Customer[]> {
    if (!trim(ssn)) {
      return throwError(() => "ssn undefined");
    }

    return this.#http
      .get<PaginatedResult<Customer>>(
        `${environment.snoots.api.directory}/customers`,
        {
          params: omitBy(
            {
              ssn,
              s: pageSize
            },
            isUndefined
          )
        }
      )
      .pipe(map((result) => result.docs));
  }

  searchByVatNumber(vatNumber: string): Observable<Customer[]> {
    if (!trim(vatNumber)) {
      return throwError(() => new Error("vatNumber undefined"));
    }

    return this.#http
      .get<PaginatedResult<Customer>>(
        `${environment.snoots.api.directory}/customers`,
        {
          params: {
            vatNumber
          }
        }
      )
      .pipe(map((result) => result.docs));
  }

  @UserNotify({
    successMessage: marker("Cliente creato"),
    errorMessage: marker("Non è stato possibile creare il cliente")
  })
  create(data: CustomerCreate): Observable<Customer> {
    return this.#http.post<Customer>(
      `${environment.snoots.api.directory}/customers`,
      data
    );
  }

  @UserNotify({
    successMessage: marker("Cliente aggiornato"),
    errorMessage: marker("Non è stato possibile aggiornare il cliente")
  })
  update(request: Partial<CustomerUpdate>, id: string): Observable<Customer> {
    return this.#http.patch<Customer>(
      `${environment.snoots.api.directory}/customers/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: (customer: Customer, patient: Pet) =>
      sntTranslate(marker("patientName collegato a customerName"), {
        customerName: getCustomerNominative(customer),
        patientName: patient.name
      }),
    errorMessage: marker("Non è stato possibile aggiungere il paziente")
  })
  addPatient(customer: Customer, patient: Pet): Observable<Customer> {
    return this.#http.patch<Customer>(
      `${environment.snoots.api.directory}/customers/${customer.id}`,
      {
        pets: [...(customer?.pets?.map((x) => x.id) || []), patient.id]
      }
    );
  }

  @UserNotify({
    successMessage: marker("Cliente rimosso"),
    errorMessage: marker("Non è stato possibile rimuovere il cliente")
  })
  delete(request: CustomerDelete): Observable<string> {
    return this.#http.delete<string>(
      `${environment.snoots.api.directory}/customers/${request.id}`
    );
  }

  @UserNotify({
    successMessage: marker("Tag aggiornati"),
    errorMessage: marker("Non è stato possibile aggiungere il tag")
  })
  updateTags(id: string, request: CustomerUpdateTags): Observable<Customer> {
    return this.#http.patch<Customer>(
      `${environment.snoots.api.directory}/customers/${id}`,
      request
    );
  }

  loadTags(request: CustomerLoadTags): Observable<string[]> {
    const customer = this.#http.get<Customer>(
      `${environment.snoots.api.directory}/customers/${request.id}`
    );

    return customer.pipe(map((customer) => customer.tags || []));
  }

  fullTextSearch(
    query: string,
    pageSize?: number,
    options?: { fuzzy?: boolean }
  ): Observable<Customer[]> {
    return this.#http
      .get<PaginatedResult<Customer> | Customer[]>(
        `${environment.snoots.api.directory}/customers`,
        {
          params: omitBy(
            {
              q: query,
              s: pageSize,
              fuzzy: options?.fuzzy ? true : undefined
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((result: PaginatedResult<Customer> | Customer[]) =>
          isArray(result) ? result : result.docs || []
        )
      );
  }

  listProperties(): Observable<string[]> {
    return this.#http.get<string[]>(
      `${environment.snoots.api.directory}/properties/customer`
    );
  }

  @UserNotify({
    successMessage: marker("Consenso privacy aggiornato"),
    errorMessage: marker("Non è stato possibile aggiornare il consenso privacy")
  })
  signPrivacyPolicy(id: string): Observable<void> {
    return this.#http.patch<void>(
      `${environment.snoots.api.directory}/customers/${id}`,
      {
        privacyPolicy: true
      }
    );
  }

  loadAttachments(id: string): Observable<AttachmentV2[]> {
    return this.#http
      .get<AttachmentV2[]>(
        `${environment.snoots.api.directory}/customers/${id}/attachments`
      )
      .pipe(
        map((attachments) => {
          return attachments.map((x) => ({
            ...x,
            extension: x.name?.endsWith(".pdf") ? "pdf" : undefined
          }));
        })
      );
  }

  addAttchment({
    id,
    name,
    storageId,
    type,
    contentStorageId,
    signable
  }: {
    id: string;
    name: string;
    storageId: string;
    type: string;
    signable: boolean;
    contentStorageId?: string;
  }): Observable<{ id: string }> {
    return this.#http.post<{ id: string }>(
      `${environment.snoots.api.directory}/customers/${id}/attachments`,
      {
        name: stringRemoveNonAsciiChar(name),
        storageId,
        contentStorageId,
        type,
        signable
      }
    );
  }

  @UserNotify({
    successMessage: marker("Contatti uniti correttamente"),
    errorMessage: marker("Non è stato possibile unire i contatti"),
    ignoreErrorCode: [409]
  })
  mergeCustomers(
    idCustomerA: string,
    idCustomerB: string,
    maintainedProperties?: string[]
  ): Observable<void> {
    return this.#http.put<void>(
      `${environment.snoots.api.directory}/customers/${idCustomerA}/merge`,
      {
        customerId: idCustomerB,
        maintainedProperties: maintainedProperties
      }
    );
  }

  @UserNotify({
    successMessage: marker("Contatti esportati in Excel"),
    errorMessage: marker("Impossibile esportare i contatti"),
    progressIndeterminate: true,
    progressMessage: marker("Esportazione contatti in corso")
  })
  exportMassiveExcel(exportOptions: {
    filter?: { tags?: string[] };
    includeIds?: string[];
    excludeIds?: string[];
  }): Observable<void> {
    return this.#http.post<void>(
      `${environment.snoots.api.directory}/customers/exports`,
      exportOptions
    );
  }
}
